import Button from '@lyra/core/components/Button'
import ButtonGroup from '@lyra/core/components/ButtonGroup'
import DropdownButton from '@lyra/core/components/DropdownButton'
import Responsive from '@lyra/core/components/Responsive'
import Tooltip from '@lyra/core/components/Tooltip'
import { SECONDS_IN_DAY } from '@lyra/core/constants/time'
import formatDate from '@lyra/core/utils/formatDate'
import formatDateTime from '@lyra/core/utils/formatDateTime'
import useOrderbookTimestamp from '@lyra/web/hooks/useOrderbookTimestamp'
import { getUtcSecs } from '@lyra/web/utils/time'
import { StackProps } from 'tamagui'

type Props = {
  selectedExpiry: Date
  expiries: Date[]
  onChangeExpiry: (expiry: Date) => void
} & StackProps

const TradeOptionTableExpiryButtonGroup = ({
  selectedExpiry,
  expiries,
  onChangeExpiry,
  ...stackProps
}: Props) => {
  const orderbookTimestamp = useOrderbookTimestamp().getUtcTimestampSec()
  return (
    <Responsive
      desktop={
        <ButtonGroup {...stackProps}>
          {expiries.map((expiry) => {
            const isSameYear = expiry.getUTCFullYear() === new Date().getUTCFullYear()
            const daysToExpiry = Math.ceil(
              (getUtcSecs(expiry) - orderbookTimestamp) / SECONDS_IN_DAY
            )
            const isDte = daysToExpiry <= 2

            const button = (
              <Button
                key={expiry.getTime()}
                isSelected={selectedExpiry.getTime() === expiry.getTime()}
                onPress={() => onChangeExpiry(expiry)}
                minWidth={isSameYear ? 70 : 90}
                label={
                  isDte
                    ? `${daysToExpiry - 1} DTE`
                    : formatDate(expiry, {
                        format: isSameYear ? 'd mmm' : 'd mmm yyyy',
                      })
                }
              />
            )

            if (isDte) {
              return (
                <Tooltip
                  key={expiry.getTime()}
                  trigger={button}
                  title={`${daysToExpiry - 1} Day${
                    daysToExpiry - 1 !== 1 ? 's' : ''
                  } until Expiration`}
                  content={`These options expire in less than ${daysToExpiry} day${
                    daysToExpiry !== 1 ? 's' : ''
                  } at ${formatDateTime(expiry, { hideYear: true })}.`}
                  placement="bottom-start"
                />
              )
            }

            return button
          })}
        </ButtonGroup>
      }
      mobile={
        <DropdownButton
          label={formatDate(selectedExpiry, { skipYear: true })}
          dropdownProps={{ minWidth: 120 }}
        >
          {expiries.map((expiry) => {
            return (
              <DropdownButton.ListItem
                key={expiry.getTime()}
                label={formatDate(expiry, { skipYear: true })}
                onPress={() => onChangeExpiry(expiry)}
                isSelected={selectedExpiry.getTime() === expiry.getTime()}
              />
            )
          })}
        </DropdownButton>
      }
    />
  )
}

export default TradeOptionTableExpiryButtonGroup
